import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { Router } from '@angular/router'
import { AccountType } from '../../interfaces/api/AccountType'
import { ApiService, UserInfo } from '../../services/api.service'
import { CommonModule, NgIf } from '@angular/common'
import { environment } from '../../../environments/environment'
import { CosentinoSvgComponent } from '@components/cosentino-svg/cosentino-svg.component'
import { WE_LOCALES } from 'app/app.module'
import { LoadingService } from '../../services/loading.service'
import { CheckboxToggleComponent } from '@components/checkbox-side/checkbox-toggle.component'
import { ButtonComponent } from '@components/button/button.component'
import { MenuService } from '../../services/menu.service'
import { MenuItemType } from '../../interfaces/api/MenuItemType'

type Lang = { code: string; label: string }

@Component({
    selector: 'app-top-bar',
    templateUrl: './top-bar.component.html',
    styleUrl: './top-bar.component.scss',
    imports: [TranslateModule, NgIf, CosentinoSvgComponent, CommonModule, CheckboxToggleComponent,ButtonComponent],
    standalone: true,
})
export class TopBarComponent implements OnInit {
    @Input()
    isMobileMenuOpen: boolean

    @Output()
    onToggleMobileMenu = new EventEmitter<void>()

    isProfilePopupOpen: boolean = false
    allowAccountAccess?: boolean = false
    showDisclaimer:boolean = false

    userInfo: UserInfo
    // nombres “bonitos” (fallback al código si no está mapeado)
    // nombres base por idioma
    private names: Record<string, string> = {
        cs: 'Čeština',
        da: 'Dansk',
        de: 'Deutsch',
        en: 'English (United States)',
        'en-au': 'English (Australia)',
        'en-ca': 'English (Canada)',
        'en-gb': 'English',
        es: 'Español',
        fi: 'Suomi',
        fr: 'Français',
        'fr-ca': 'Français (Canada)',
        hr: 'Hrvatski',
        hu: 'Magyar',
        it: 'Italiano',
        iw: 'עברית', // Nota: 'iw' es hebreo (código legacy) = 'he'
        ja: '日本語',
        nl: 'Nederlands',
        no: 'Norsk',
        pl: 'Polski',
        pt: 'Português',
        sl: 'Slovenščina',
        sk: 'Slovenčina',
        sv: 'Svenska',
        tr: 'Türkçe',
    }
    languages: Lang[] = WE_LOCALES.map((code) => ({
        code,
        label: this.names[code] ?? code.toUpperCase(),
    }))

    constructor(
        private router: Router,
        private api: ApiService,
        public translate: TranslateService,
        private loading: LoadingService,
        private menuService: MenuService
    ) {}

    private currentCode(): string {
        const lang = this.translate.currentLang || this.translate.getDefaultLang() || 'en'
        return lang.toLowerCase()
    }

    get currentLangLabel(): string {
        const code = this.currentCode()
        // Try exact match first, then fallback to base language
        const exactMatch = this.languages.find((l) => l.code === code)
        if (exactMatch) return exactMatch.label
        
        const baseCode = code.split('-')[0]
        return this.languages.find((l) => l.code === baseCode)?.label ?? this.names[baseCode] ?? code.toUpperCase()
    }

    isActive(code: string): boolean {
        return this.currentCode() === code.toLowerCase()
    }

    changeLang(code: string) {
        let sfCode = code;
        if(code.includes('-')){
            const codeLang = code.split('-')[0]
            const codeCountry = code.split('-')[1]
            sfCode = codeLang + '_' + codeCountry.toUpperCase()
        }
        this.api.updateLanguage(sfCode).subscribe({
            next: () => {
                localStorage.setItem('we-locale', code) // recuerda la preferencia
                
                // Update userInfo.locale locally to keep it in sync
                const currentUserInfo = this.api.userInfo.getValue()
                if (currentUserInfo) {
                    currentUserInfo.locale = sfCode
                    this.api.updateUserInfo(currentUserInfo)
                }
                
                this.translate.use(code) // aplica inmediatamente
                // Reload page to update date, number and currency pipes
                const currentUrl = this.router.url;
                
                // Build URL with language param for consistent LOCALE_ID
                // Keep the full language code (e.g., en-au, fr-ca)
                let lang = code || 'en'
                
                if(environment.webComponent){
                    this.loading.show()
                    setTimeout(() => {
                        this.loading.hide()
                        const url = new URL(window.location.href)
                        if (url.searchParams.has('language')) {
                            url.searchParams.set('language', lang)
                        } else {
                            url.searchParams.append('language', lang)
                        }
                        window.location.href = url.toString()
                    }, 1000)
                } else {
                    // Also set language param in URL for consistent LOCALE_ID on reload
                    const url = new URL(window.location.href)
                    if (url.searchParams.has('language')) {
                        url.searchParams.set('language', lang)
                    } else {
                        url.searchParams.append('language', lang)
                    }
                    window.location.href = url.toString()
                }
            },
            error: (e) => {
                console.error('Error al cambiar el idioma', e)
            }
        })
    }
    ngOnInit() {
        this.api.userInfo.subscribe((userInfo) => ((this.userInfo = userInfo), (this.allowAccountAccess = userInfo.allowImpersonateAccess)))
    }

    goHome() {
        this.menuService.goTo({
            type: MenuItemType.ROUTE,
            code: 'home',
            link: null,
        })
    }

    toggleProfilePopup() {
        this.isProfilePopupOpen = !this.isProfilePopupOpen
    }
    onAllowAccessChange(allowed: boolean) {
        this.allowAccountAccess = allowed
        this.api.updateAccountAllowAccess(allowed).subscribe()
    }
    goToProfile() {
        this.isProfilePopupOpen = false
        this.menuService.goTo({
            type: MenuItemType.ROUTE,
            code: 'profile',
            link: null
        })
    }

    logout() {
        console.log('Logging out...')
        this.api.logout()
    }

    protected readonly AccountType = AccountType
    protected readonly environment = environment
}
